import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Tooltip,
  Checkbox,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ColorLegend from "./ColorLegend";
import DataTable from "./DataTable";
import { styles } from "./DataTable.styles";
import ScreenRotationIcon from "@mui/icons-material/ScreenRotation";
import Alert from "@mui/material/Alert";
import { FONT_SIZES } from "../../constants/fontSizes";
import colors from "../../constants/colors";

const getScoreColor = (score) => {
  if (score < 2) return styles.highRiskColor; // Red - Poor
  if (score < 4) return styles.mediumRiskColor; // Orange - Good
  return styles.lowRiskColor; // Green - Excellent
};

const columnTooltips = {
  mean_brand_score: "Average score across all brand name metrics",
  trademark_score:
    "Overall trademark availability and risk score. The value is inverted from trademark table to represent 'goodness' while trademark table is 'risk'",
  domain_score: "Overall domain availability and utility score",
  vibe: "The overall feel or aesthetic of the name",
  exact_domain_count: "Number of exact domain names found",
};

const tooltipStyles = {
  tooltip: {
    sx: {
      fontSize: FONT_SIZES.TEXT_HINT.xs,
      "@media (min-width: 600px)": {
        fontSize: FONT_SIZES.TEXT_HINT.sm,
      },
    },
  },
};

// Utility helpers to convert numeric scores to display labels and colors
const qualityScoreThreshold = 4.2;
const getQualityInfo = (score) => {
  const val = parseFloat(score) || 0;
  if (val >= qualityScoreThreshold)
    return { label: "Excellent", color: styles.lowRiskColor.backgroundColor };
  if (val >= 2) return { label: "Good", color: colors.sky_blue[400] };
  return { label: "Decent", color: "#B0B0B0" };
};

const highRiskThreshold = 1;
const lowRiskThreshold = 2.5;

const getRiskInfo = (score) => {
  const val = parseFloat(score) || 0;

  if (val <= highRiskThreshold)
    return { label: "High", color: styles.highRiskColor.backgroundColor };
  if (val >= lowRiskThreshold)
    return { label: "Low", color: styles.lowRiskColor.backgroundColor };
  else
    return { label: "Medium", color: styles.mediumRiskColor.backgroundColor };
};

const domainScoreThreshold = 1;

const getDomainIcon = (score) => {
  const val = parseFloat(score) || 0;

  return val >= domainScoreThreshold ? (
    <CheckCircleIcon sx={{ color: styles.lowRiskColor.backgroundColor }} />
  ) : (
    <CancelIcon sx={{ color: styles.highRiskColor.backgroundColor }} />
  );
};

const getDomainAvailabilityText = (score) => {
  const val = parseFloat(score) || 0;
  return val >= domainScoreThreshold ? "has" : "does not have";
};

// Helper to sort by domain_score DESC, then mean_brand_score DESC, then trademark_score DESC
const sortFullReportData = (data) => {
  return [...data].sort((a, b) => {
    // Sort by domain_score DESC, then mean_brand_score DESC, then trademark_score DESC
    const domainDiff =
      (parseFloat(b.exact_domain_count) || 0) -
      (parseFloat(a.exact_domain_count) || 0);
    if (domainDiff !== 0) return domainDiff;
    const brandDiff =
      (parseFloat(b.mean_brand_score) || 0) -
      (parseFloat(a.mean_brand_score) || 0);
    if (brandDiff !== 0) return brandDiff;
    return (
      (parseFloat(b.trademark_score) || 0) -
      (parseFloat(a.trademark_score) || 0)
    );
  });
};

const FullReportSummary = ({
  fullReportData,
  getDetailedRowColor,
  expandAccordian,
  onAccordionChange,
  isMobile,
  showFullReport,
  selectedRows,
  handleRowSelect,
  handleSelectAll,
  flipColumns,
}) => {
  const numericWidth = "10%";
  // Sort the data for both the table and the summary
  const sortedData = sortFullReportData(fullReportData);

  // Modern summary for top 3 suggestions
  const top3 = sortedData.slice(0, 3);

  const summaryBoxStyle = {
    background: colors.blue_green[100],
    borderRadius: "16px",
    padding: isMobile ? "12px 8px" : "20px 32px",
    margin: isMobile ? "8px 0 16px 0" : "16px 0 32px 0",
    boxShadow: "0 2px 8px rgba(13, 148, 138, 0.08)",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: isMobile ? "8px" : "12px",
  };
  const summaryTitleStyle = {
    fontWeight: 700,
    fontSize: isMobile ? FONT_SIZES.SUBTITLE.xs : FONT_SIZES.SUBTITLE.sm,
    color: colors.blue_green[400],
    marginBottom: isMobile ? "4px" : "8px",
  };
  const summaryItemStyle = {
    fontSize: isMobile ? FONT_SIZES.PARAGRAPH.xs : FONT_SIZES.PARAGRAPH.sm,
    color: colors.prussian_blue[200],
    lineHeight: 1.5,
    background: colors.white.DEFAULT,
    borderRadius: "12px",
    padding: isMobile ? "6px 8px" : "8px 16px",
    boxShadow: "0 1px 4px rgba(13, 148, 138, 0.04)",
    marginBottom: isMobile ? "2px" : "4px",
  };

  // Reorder columns: Brand Quality, Trademark Risk, Domain Availability
  const fullReportColumns = [
    {
      id: "select",
      label: "Add to Input",
      width: "5%",
      format: (_, row) => (
        <div style={styles.checkboxContainer}>
          <Checkbox
            size="small"
            checked={selectedRows.has(row.name)}
            onChange={() => handleRowSelect(row.name)}
          />
        </div>
      ),
      headerFormat: () => {
        return (
          <div style={styles.checkboxHeaderContainer}>
            {!isMobile && <span>Add to Input</span>}
            <Checkbox
              size="small"
              checked={
                sortedData.length > 0 &&
                sortedData.every((row) => selectedRows.has(row.name))
              }
              indeterminate={
                sortedData.some((row) => selectedRows.has(row.name)) &&
                !sortedData.every((row) => selectedRows.has(row.name))
              }
              onChange={handleSelectAll}
            />
          </div>
        );
      },
    },
    {
      id: "name",
      label: "Candidate Name",
      width: "20%",
    },
    {
      id: "mean_brand_score",
      label: "Brand Quality",
      width: numericWidth,
      tooltip: columnTooltips.mean_brand_score,
      tooltipProps: tooltipStyles,
      format: (value) => {
        const { label, color } = getQualityInfo(value);
        if (isMobile) {
          return (
            <span
              style={{
                display: "inline-block",
                width: 18,
                height: 18,
                borderRadius: "50%",
                backgroundColor: color,
                margin: "0 auto",
              }}
            />
          );
        }
        return (
          <span
            style={{
              backgroundColor: color,
              color: "#fff",
              padding: "2px 8px",
              borderRadius: "12px",
              display: "inline-block",
              fontWeight: "bold",
              fontSize: isMobile
                ? FONT_SIZES.TEXT_HINT.xs
                : FONT_SIZES.TEXT_HINT.sm,
              minWidth: "60px",
            }}
          >
            {label}
          </span>
        );
      },
    },
    {
      id: "trademark_score",
      label: "Trademark Risk",
      width: numericWidth,
      tooltip: columnTooltips.trademark_score,
      tooltipProps: tooltipStyles,
      format: (value) => {
        const { label, color } = getRiskInfo(value);
        if (isMobile) {
          return (
            <span
              style={{
                display: "inline-block",
                width: 18,
                height: 18,
                borderRadius: "50%",
                backgroundColor: color,
                margin: "0 auto",
              }}
            />
          );
        }
        return (
          <span
            style={{
              backgroundColor: color,
              color: "#fff",
              padding: "2px 8px",
              borderRadius: "12px",
              display: "inline-block",
              fontWeight: "bold",
              fontSize: isMobile
                ? FONT_SIZES.TEXT_HINT.xs
                : FONT_SIZES.TEXT_HINT.sm,
              minWidth: "60px",
            }}
          >
            {label}
          </span>
        );
      },
    },
    {
      id: "exact_domain_count",
      label: "Domain Availability",
      width: numericWidth,
      tooltip: columnTooltips.exact_domain_count,
      tooltipProps: tooltipStyles,
      format: (value) => getDomainIcon(value),
    },
  ];

  if (!showFullReport) {
    return (
      <>
        <Typography variant="h6" sx={styles.reportTitle}>
          Full Report Summary
        </Typography>
        <Alert severity="info" icon={<ScreenRotationIcon />} sx={styles.alert}>
          Rotate your device to landscape mode to view report table.
        </Alert>
      </>
    );
  }

  console.log("fullReportData", fullReportData);
  console.log(
    "exact_domain_count values:",
    fullReportData.map((row) => ({
      name: row.name,
      exact_domain_count: row.exact_domain_count,
      type: typeof row.exact_domain_count,
    }))
  );
  return (
    <Accordion
      expanded={expandAccordian}
      onChange={onAccordionChange}
      sx={{ ...styles.accordion, ...styles.printStyles.accordion }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6" sx={styles.accordionTitle}>
          Full Report Summary
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: { xs: 1, sm: 2 } }}>
        {/* Modern Top 3 Suggestions Summary */}
        <div>
          <div style={summaryTitleStyle}>Top 3 Suggestions</div>
          {top3.map((row, idx) => {
            const brandQuality = getQualityInfo(row.mean_brand_score).label;
            const trademarkRisk = getRiskInfo(row.trademark_score).label;
            const domainAvailability = getDomainAvailabilityText(
              row.exact_domain_count
            );
            // Determine emoji
            let emoji = "";
            const isWarning =
              domainAvailability === "not have" ||
              trademarkRisk === "High" ||
              brandQuality === "Decent";
            if (isWarning) {
              emoji = "🤔";
            } else if (idx === 0) {
              emoji = "🔥";
            } else if (idx === 1) {
              emoji = "☀️";
            } else if (idx === 2) {
              emoji = "🌸";
            }
            return (
              <div key={row.name} style={summaryItemStyle}>
                <span style={{ marginRight: 8 }}>{emoji}</span>
                <b>{row.name}</b>:{" "}
                <span>{row.vibe || "No vibe provided."}</span> Brand name
                quality is <b>{brandQuality}</b> with a <b>{trademarkRisk}</b>{" "}
                trademark risk, and <b>{domainAvailability}</b> available domain
                names.
              </div>
            );
          })}
        </div>
        {/* Table */}
        <DataTable
          columns={fullReportColumns}
          data={sortedData}
          getRowColor={getDetailedRowColor}
          isReportData={false}
          sx={{ ...styles.dataTable, ...styles.printStyles.dataTable }}
          isMobile={isMobile}
          flipColumns={flipColumns}
          isDomainUtility={true}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default FullReportSummary;
